import CustomDrawer from '@Modules/core/components/CustomDrawer';
import CustomFieldSet from '@Modules/core/components/CustomFieldSet';
import React from 'react';
import { ChakraComponents as CC } from '@Modules/core/lib/chakra';
import { useQuery } from '@tanstack/react-query';
import { Divider, Skeleton, StackDivider, useToast } from '@chakra-ui/react';

type THeading = {
  accountData: {
    readonly bankID: 'ID Banque',
    readonly bank: 'Nom Banque',
    readonly hpxAccount: 'Compte HPX',
    readonly bankAccount: 'Compte Bancaire',
    readonly customer: 'Client',
    [x: string]: any,
  },
  applicationData: {
    readonly accountType: 'Type Compte',
    readonly accountStatus: 'Statut Compte',
    readonly statusDate: 'Date Statut',
    readonly accountCurrency: 'Monnaie Compte',
    readonly balance: 'Solde',
    readonly creationDate: 'Date Création',
    readonly effectiveDate: 'Date Effective',
    readonly closingDate: 'Date Fermeture',
    readonly bankAccountType: 'Type Compte Bancaire',
    [x: string]: any,
  },
};

const heading: THeading = {
  accountData: {
    bankID: 'ID Banque',
    bank: 'Nom Banque',
    hpxAccount: 'Compte HPX',
    bankAccount: 'Compte Bancaire',
    customer: 'Client',
  },
  applicationData: {
    accountType: 'Type Compte',
    accountStatus: 'Statut Compte',
    statusDate: 'Date Statut',
    accountCurrency: 'Monnaie Compte',
    balance: 'Solde',
    creationDate: 'Date Création',
    effectiveDate: 'Date Effective',
    closingDate: 'Date Fermeture',
    bankAccountType: 'Type Compte Bancaire',
  },
};

type Props = {
  accountId: string | number; // Use this property to fetch the data from the database
};

const AccountDataDrawer: React.FC<Props> = ({accountId}) => {
  
  const toast = useToast();

  //   Initialize react Query to GET Data
  const queryKey = [`Account.${accountId}.Data`];
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      // TODO: Replace this by your fetcher function
      return new Promise(function (resolve) {
        setTimeout(() => resolve({}), 1500);
      });
    },
    onSuccess: (data_: any) => {
      // console.log(data_);
    },
    onError: (data_: any) => {
      toast({
        position: 'top-right',
        title:
          "Une erreur inattendue s'est produite lors de la récupération des données",
        isClosable: true,
        status: 'error',
      });
    },
  });

  const drawerContent = (
    <>
      <CustomFieldSet title="Données Compte">
        <CC.SimpleGrid columns={2} spacing={5}>
          {Object.keys(heading.accountData).map((key_) => (
            <CC.Box key={key_}>
              <CC.Heading size="xs" textTransform="none">
                {heading.accountData[key_]}
              </CC.Heading>
              <Skeleton isLoaded={!isLoading}>
                {key_ !== "customer" ? 
                  <CC.Text pt="2" fontSize="sm">
                    {data && key_ in data && data[key_] || 'xxxx'}
                  </CC.Text> : 
                  <CC.HStack spacing={16} fontSize={"sm"}>
                    <CC.Text pt="2">
                    {data && key_ in data && data[key_].code || 'xxxx'}
                    </CC.Text>
                    <CC.Text pt="2">
                      {data && key_ in data && data[key_].name || 'xxxx'}
                    </CC.Text>
                  </CC.HStack>
                }
              </Skeleton>
            </CC.Box>
          ))}
        </CC.SimpleGrid>
      </CustomFieldSet>

      <CC.Divider my={5} />

      <CustomFieldSet title="Donnée Application">
        <CC.SimpleGrid columns={2} spacing={5}>
          {Object.keys(heading.applicationData).map((key_) => (
            <CC.Box key={key_}>
              <CC.Heading size="xs" textTransform="none">
                {heading.applicationData[key_]}
              </CC.Heading>

              <Skeleton isLoaded={!isLoading}>
              {key_ !== "bankAccountType" ? 
                  <CC.Text pt="2" fontSize="sm">
                    {data && key_ in data && data[key_] || 'xxxx'}
                  </CC.Text> : 
                  <CC.HStack spacing={16} fontSize={"sm"}>
                    <CC.Text pt="2">
                      {data && key_ in data && data[key_].code || 'xxxx'}
                    </CC.Text>
                    <CC.Text pt="2">
                      {data && key_ in data && data[key_].name || 'xxxx'}
                    </CC.Text>
                  </CC.HStack>
                }
              </Skeleton>
            </CC.Box>
          ))}
        </CC.SimpleGrid>
      </CustomFieldSet>
    </>
  );

  return drawerContent;
};

export default AccountDataDrawer;
